import { useEffect, useState, type FunctionComponent } from 'react';
import type { VideoFieldsFragment } from '../../../__generated__/graphql-client-types';
import { NO_IMAGE_URL } from '../../../constants/home/general';
import { windowIsNotSmall } from '../../../helpers/breakpoints/breakpoint.helpers';
import { type CloudinaryOptions } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import type { NormalizedConstructImage } from '../../../helpers/construct/page-content.helper';
import { type AnimatedSplashCallToAction } from '../../../types/home.types';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import { EmbeddedVideo } from '../../common-components/embedded-video/embedded-video.component';
import { Link } from '../../common-components/link/link.component';
import { PictureImage } from '../../images/picture-image/picture-image.component';
import { ctaButtonPosition, ctaForegroundSize, splashAspectRatio } from '../animated-splash/animated-splash.css';
import { MainImage } from '../home-splash/main-image/main-image.component';
import { translateAlignmentToStyles } from '../home-splash/splash-helpers/splash.helpers';

const SPLASH_WIDTH = 1280;
const SPLASH_HEIGHT = 512;

type CallToActionContentProps = {
	callToAction: AnimatedSplashCallToAction;
};

const CallToActionContent: FunctionComponent<CallToActionContentProps> = ({ callToAction }) => {
	const { ctaStyle, ctaSubtext, ctaTitle, foregroundTracking, text } = callToAction;
	const { url, tracking } = callToAction.link;
	const resolvedForegroundTracking = foregroundTracking ? foregroundTracking : tracking;
	const ctaForegroundColor = ctaStyle === 'LIGHT' ? 'bg-theme-primary-lighter' : 'bg-theme-primary-darker';
	const ctaSubtextColor = ctaStyle === 'LIGHT' ? 'theme-black' : 'theme-white';
	const buttonStyle = ctaStyle === 'LIGHT' ? 'PRIMARY' : 'SECONDARY';

	return (
		<div className="flex flex-column items-center justify-center overflow-hidden w-full p-0 md:p-8 relative">
			<div className={`absolute z-1 ${ctaButtonPosition}`}>
				<div className="dn flex-l">
					<LinkButton buttonStyle={buttonStyle} size={'DEFAULT'} url={url} analyticsHook={tracking}>
						{text}
					</LinkButton>
				</div>
				<div className="flex dn-l">
					<LinkButton buttonStyle={buttonStyle} size={'SMALL'} url={url} analyticsHook={tracking}>
						{text}
					</LinkButton>
				</div>
			</div>
			<Link
				url={url}
				ariaLabel="foreground"
				analyticsHook={resolvedForegroundTracking}
				className="flex flex-column items-center overflow-hidden"
				automationHook="landing-page-section">
				<div className={`${ctaForegroundSize} ${ctaForegroundColor} rounded-2xl`}>
					<div className={`m-8 ${ctaSubtextColor}`}>
						<h3 className="mt-0 mb-2 text-2xl lg:text-3xl font-light uppercase" style={{ letterSpacing: '2px' }}>
							{ctaTitle}
						</h3>
						<p className="my-0 lg:text-lg">{ctaSubtext}</p>
					</div>
				</div>
			</Link>
		</div>
	);
};

const FALLBACK_IMAGE_OPTIONS: CloudinaryOptions = {
	width: SPLASH_WIDTH,
	height: SPLASH_HEIGHT,
	quality: 80,
	progressive: true,
	format: 'jpg',
	type: 'private'
};

export type AnimatedSplashProps = {
	image: NormalizedConstructImage;
	fallbackImage: NormalizedConstructImage;
	video?: VideoFieldsFragment | null;
	mobileImage?: NormalizedConstructImage;
	backgroundTracking?: string | null;
	animatedSplashCallToAction: AnimatedSplashCallToAction;
};

export const AnimatedSplash: FunctionComponent<AnimatedSplashProps> = ({
	image,
	fallbackImage,
	video,
	mobileImage,
	backgroundTracking,
	animatedSplashCallToAction
}) => {
	const [showVideo, setShowVideo] = useState<boolean>(false);
	const { url, tracking } = animatedSplashCallToAction.link;
	const resolvedBackgroundTracking = backgroundTracking ? backgroundTracking : tracking;
	const hasNoImage = image.id === NO_IMAGE_URL;
	const dynamicCTAAlignmentStyle = translateAlignmentToStyles(animatedSplashCallToAction.alignment);

	useEffect(() => {
		const isNotSmall = windowIsNotSmall();
		if (isNotSmall) {
			setShowVideo(true);
		}
	}, []);

	return (
		<section className="flex flex-column items-center w-100 min-h-100 relative pointer bg-theme-white" data-testid="animated-splash">
			<div className="flex flex-column items-center overflow-hidden w-100 relative br4" style={{ maxHeight: SPLASH_HEIGHT }}>
				<div className="absolute dn flex-ns z-1" style={{ ...dynamicCTAAlignmentStyle }}>
					<CallToActionContent callToAction={animatedSplashCallToAction} />
				</div>
				<div className="absolute dn-ns flex z-1 mt6">
					<CallToActionContent callToAction={animatedSplashCallToAction} />
				</div>
				{hasNoImage ? (
					<div className={`relative w-100 ${splashAspectRatio}`}>
						<div className="absolute mh3 mh0-ns" style={{ inset: 0 }}>
							<Link url={url}>
								<PictureImage
									description={fallbackImage.description}
									publicID={fallbackImage.id}
									options={FALLBACK_IMAGE_OPTIONS}
									className="br4 max-h-100"
									loading="eager"
									imageProps={{ width: SPLASH_WIDTH, height: SPLASH_HEIGHT }}
									artDirectionVariations={
										mobileImage
											? [
													{
														mediaCondition: { maxWidth: 767 },
														options: { type: 'private' },
														publicID: mobileImage.id
													}
											  ]
											: []
									}
								/>
							</Link>
						</div>
						{showVideo && video?.id && (
							<div className="absolute" style={{ inset: 0 }} data-testid="splash-video">
								<Link url={url} className="w-100 h-100">
									<div className="disableClickEvents">
										<EmbeddedVideo video={video} shouldLoadEagerly={true} />
									</div>
								</Link>
							</div>
						)}
					</div>
				) : image ? (
					<MainImage
						background={image}
						url={url}
						analyticsHook={resolvedBackgroundTracking}
						options={{ ...FALLBACK_IMAGE_OPTIONS, format: 'auto' }}
					/>
				) : null}
			</div>
		</section>
	);
};
